import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, LinearProgress, Stack, Typography} from '@mui/material';
import {defaultMarkdownParser} from 'prosemirror-markdown';
import {useReportStore} from '../../useReportStore';
import GeneratedReportContent from './GeneratedReportContent';
import ReasoningBox from './ReasoningBox';

const BOTTOM_EPSILON = 64;

/**
 * GeneratedContentContainer - A container component for displaying streamed generated content
 * with automatic scrolling behavior similar to MessagesContainer.
 */
const GeneratedContentContainer = ({
                                       markdown,
                                       error,
                                       isGenerating,
                                       reasoningSteps = [],
                                       isReasoning = false,
                                       onReplace,
                                   }) => {
    const scrollRef = useRef(null);
    const contentRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const editorView = useReportStore(s => s.editorView);

    const scrollToBottom = useCallback((useSmooth = false) => {
        const container = scrollRef.current;
        if (!container) return;
        if (useSmooth) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        } else {
            // Direct assignment for instant scroll (streaming, resize)
            container.scrollTop = container.scrollHeight;
        }
    }, []);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Consider within BOTTOM_EPSILON of bottom as "at bottom"
        const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < BOTTOM_EPSILON;
        setIsAtBottom(atBottom);
    }, []);

    const handleReplaceGenerated = useCallback(() => {
        if (!editorView) {
            console.error('No editor view available');
            return;
        }

        const {state, dispatch} = editorView;
        const {schema, doc} = state;

        const authorMark = schema.marks.author;
        if (!authorMark) {
            console.error('Author mark type not found in schema');
            return;
        }

        const nextMarkdown = (markdown || '').trim();
        if (!nextMarkdown) return;

        // Parse markdown into a ProseMirror document.
        // We parse using the default markdown parser (based on the basic schema), then
        // re-create each top-level node in our editor schema so we keep our custom attrs
        // (blockId, classificationLevel, etc.).
        let parsed;
        try {
            parsed = defaultMarkdownParser.parse(nextMarkdown);
        } catch (e) {
            console.error('Failed to parse markdown:', e);
            return;
        }

        const aiAuthorMark = authorMark.create({userId: 'ai'});

        // Map parsed document content to this editor's schema.
        // (We intentionally keep a conservative subset: headings, paragraphs, lists, blockquotes, code blocks, tables.)
        const mappedTopLevel = [];
        parsed.content.forEach((node) => {
            try {
                const mapped = schema.nodeFromJSON(node.toJSON());
                mappedTopLevel.push(mapped);
            } catch (e) {
                // Fallback: if a node type isn't supported by our schema, convert it to a paragraph of text.
                const text = node.textContent || '';
                mappedTopLevel.push(schema.nodes.paragraph.create(null, schema.text(text)));
            }
        });

        const docStart = 0;
        const docEnd = doc.content.size;

        let tr = state.tr;
        tr = tr.replaceWith(docStart, docEnd, mappedTopLevel);

        // Apply author mark across the entire inserted range.
        // After replaceWith, the new document is in tr.doc.
        const insertedFrom = 0;
        const insertedTo = tr.doc.content.size;
        tr = tr.addMark(insertedFrom, insertedTo, aiAuthorMark);

        // Skip author plugin (we've already set author marks manually)
        tr = tr.setMeta('skipAuthorPlugin', true);

        dispatch(tr);

        try {
            editorView.focus();
        } catch (_) {
            // noop
        }

        // Close the modal (or any parent UI) after applying the replacement.
        try {
            onReplace?.();
        } catch (e) {
            console.error('onReplace callback failed:', e);
        }
    }, [editorView, markdown, onReplace]);

    // Watch content for height changes (streaming content growing)
    useEffect(() => {
        const container = scrollRef.current;
        const content = contentRef.current;
        if (!container || !content || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver(() => {
            if (isAtBottom) {
                // Keep stuck to bottom while streaming or at bottom
                container.scrollTop = container.scrollHeight;
            }
        });

        ro.observe(content);
        return () => ro.disconnect();
    }, [isAtBottom]);

    // Auto-scroll to bottom when content changes during streaming
    useEffect(() => {
        if (isGenerating && isAtBottom) {
            scrollToBottom(false);
        }
    }, [markdown, reasoningSteps, isGenerating, isAtBottom, scrollToBottom]);

    const canShowReplace = Boolean(
        editorView &&
        !isGenerating &&
        !isReasoning &&
        !error &&
        (markdown && markdown.trim().length > 0)
    );

    const showReplaceAlert = Boolean(
        // "Visible all the time" means: whenever there's generated content to review.
        // Keep it hidden before the first generation, and during reasoning-only phase.
        !isReasoning &&
        !error &&
        (markdown && markdown.trim().length > 0)
    );

    const ReplaceAlert = showReplaceAlert ? (
        <Alert
            severity="success"
            action={
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleReplaceGenerated}
                    size="small"
                    disabled={!canShowReplace}
                >
                    Replace
                </Button>
            }
        >
            A separate history version will be created, so you can find it under 'preview'.
        </Alert>
    ) : null;

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
                overflowY: 'auto',
                height: '100%',
                minHeight: 0,
                flex: 1,
            }}
        >
            <Stack ref={contentRef} p={4} spacing={1}>


                {/* Reasoning Box - shows before/during report generation */}
                {(reasoningSteps.length > 0 || isReasoning) && (
                    <ReasoningBox
                        reasoningSteps={reasoningSteps.length ? reasoningSteps : ['Thinking...']}
                        isActive={isReasoning}
                    />
                )}

                {!isGenerating && !markdown && !error && reasoningSteps.length === 0 ?
                    <Typography variant="body2" color="text.secondary">
                        Generated content will appear here once you start generation.
                    </Typography>
                    :
                    (!isReasoning &&
                        <Stack py={3} spacing={1}>
                            <Typography variant="body2" color={'text.secondary'}>Generated Report Content:</Typography>

                            {!isGenerating && ReplaceAlert}

                            <GeneratedReportContent
                                markdown={markdown}
                                error={error}
                            />

                            {!isGenerating && ReplaceAlert}

                            {/* Linear progress at the bottom */}
                            {isGenerating && (<LinearProgress/>)}
                        </Stack>)
                }

            </Stack>
        </Stack>
    );
};

export default GeneratedContentContainer;
